import {
  Signing,
  sha256,
  generateCommitment,
  generateJwt,
  parseJwt,
} from "@credify/crypto";

import { Entity } from "./entity";
import { Claims } from "./claims";
import { OIDC } from "./oidc";
import { Offer } from "./offer";
import { Auth } from "./auth";
import { Bnpl } from "./bnpl";
import ApiClient from "./apiClient";
import type { CredifyConfig } from "../types";
import { CredifyInstance } from "./type";
import { Intent } from "./intent";
import { PartnerOnboarding } from "./onboarding";
import { AuthenticationApiFactory } from "@credify/api-docs/apiClients/v2";
import { handleAPIConfiguration } from "./utils";
import { Configuration } from "@credify/api-docs";
import { apiBase } from "./config";
import { camelize } from "@credify/crypto";
class Credify {
  private static _instance: CredifyInstance = {};
  entity: Entity;
  claims: Claims;
  oidc: OIDC;
  offer: Offer;
  auth: Auth;
  bnpl: Bnpl;
  intent: Intent;
  partnerOnboarding: PartnerOnboarding;
  accessToken: string;

  /**
   * Instantiates Credify object
   * @param secret Signing private key (raw PKCS#8 string value).
   * @param accessToken
   * @param config
   * */
  constructor(secret: string, accessToken: string, config: CredifyConfig) {
    const signing = new Signing();
    signing.importPrivateKey(secret);
    const apiClient = new ApiClient("", accessToken, config);
    this.entity = new Entity(config, apiClient, signing);
    this.claims = new Claims(config, apiClient, signing);
    this.oidc = new OIDC(config, apiClient, signing);
    this.offer = new Offer(config, apiClient, signing);
    this.auth = new Auth(config, apiClient, signing);
    this.bnpl = new Bnpl(config, apiClient, signing);
    this.partnerOnboarding = new PartnerOnboarding(config, apiClient, signing);
    this.intent = new Intent(config, apiClient, signing);
    this.accessToken = accessToken.replace("Bearer ", "");
  }

  // Check token's expire time
  private static checkTokenExpired(accessToken: string): boolean {
    const tokenInfo = parseJwt(accessToken);
    const exp = tokenInfo?.payload?.exp;
    const current = Math.floor(new Date().getTime() / 1000); // to seconds
    // Compare with 60 seconds before current
    return exp - 60 < current;
  }

  static async create(secret: string, apiKey: string, config: CredifyConfig) {
    if (
      this._instance.hasOwnProperty(apiKey) &&
      !this.checkTokenExpired(this._instance[apiKey].accessToken)
    ) {
      return this._instance[apiKey];
    }

    const defaultConfig: CredifyConfig = { mode: "sandbox", locale: "en-US" };
    const cnf = config || defaultConfig;
    const configuration = handleAPIConfiguration(cnf.mode)
    const client = AuthenticationApiFactory(configuration)
    const tokenRequest = { key: apiKey }
    const res = await client.requestClientAuth(tokenRequest)
    const accessToken = camelize(res.data).accessToken || "";
    this._instance[apiKey] = new Credify(secret, `Bearer ${accessToken}`, cnf);
    return this._instance[apiKey];
  }
}

export { Credify, sha256, generateJwt, parseJwt, generateCommitment };
